// Marketing's container (helloitsme agency). Loaded on the signup pages only, never on clinical
// pages. Every environment loads it so the agency can test; their tags filter on the hostname.
const CONTAINER_ID = 'GTM-TXHLDQ3D'
const SCRIPT_ID = 'google-tag-manager'
// GTM confirms an event when its tags start, not once they have sent it; on a page loaded fresh
// after the Stripe redirect, they need time to load and send the conversion first.
const MIN_WAIT_MS = 2000
// Upper bound, so a browser that blocks GTM doesn't hold the user back.
const MAX_WAIT_MS = 5000

export interface SignupConversion {
    plan: string
    amount_cents: number
    currency: string
    email_sha256: string
}

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[]
    }
}

function dataLayer(): Record<string, unknown>[] {
    window.dataLayer = window.dataLayer ?? []
    return window.dataLayer
}

// Google's standard container snippet, minus the inline script our CSP would refuse.
export function loadTagManager(): void {
    if (document.getElementById(SCRIPT_ID)) return

    dataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' })
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${CONTAINER_ID}`
    document.head.appendChild(script)
}

// Loader for the app's routes. A loaded container can't be removed, so entering the app after
// signup does a full page load; the pending promise keeps the route from rendering meanwhile.
// request.url, because the loader runs before the URL changes; replace, so "back" can't rerun
// the signup completion.
export function dropTagManager({ request }: { request: Request }): Promise<never> | null {
    if (!document.getElementById(SCRIPT_ID)) return null

    window.location.replace(request.url)
    return new Promise(() => {})
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

// The event marketing's tags are built on. A retried completion pushes it again; their tags
// deduplicate on transaction_id. Settles once GTM has handled it and the tags have had time to
// send it: leaving the page earlier cancels them, and the conversion with it.
export function trackSignupConversion(checkoutSessionId: string, conversion: SignupConversion): Promise<void> {
    const handled = new Promise<void>((resolve) => {
        dataLayer().push({
            event: 'trial_signup',
            transaction_id: checkoutSessionId,
            plan: conversion.plan,
            value: conversion.amount_cents / 100,
            currency: conversion.currency,
            user_data: { sha256_email_address: conversion.email_sha256 },
            eventCallback: () => resolve(),
        })
    })

    return Promise.race([Promise.all([handled, wait(MIN_WAIT_MS)]).then(() => {}), wait(MAX_WAIT_MS)])
}
